import { useRef, useState } from 'react';
import { useLocation } from '@tanstack/react-router';
import { Box, Divider, Menu, MenuItem, Tab, Tabs, Typography } from '@mui/material';
import { usePageHeader } from '../../contexts/PageHeaderContext';
import { useOverflowTabs } from '../../hooks/useOverflowTabs';

export const PageSubHeader = () => {
  const { title, navButtons, onNavigate, headerActions } = usePageHeader();
  const location = useLocation();
  const visibleButtons = navButtons?.filter((button) => !!button.link) ?? [];
  const hasNav = visibleButtons.length > 0;
  const currentTabIndex = visibleButtons.findIndex((button) => {
    const link = (button.link ?? '').replace(/\/$/, '');
    if (!link) return false;
    const here = location.pathname.replace(/\/$/, '');
    return here === link || here.startsWith(link + '/');
  });
  const tabsRef = useRef<HTMLDivElement>(null);
  const measurementRef = useRef<HTMLDivElement>(null);
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
  const { displayedIndices, hiddenIndices, hasOverflow } = useOverflowTabs({
    itemCount: visibleButtons.length,
    activeIndex: currentTabIndex,
    containerRef: tabsRef,
    measurementRef,
    moreWidth: 94,
  });

  if (!title && !hasNav) return null;

  return (
    <Box sx={{ px: 3, flexShrink: 0 }}>
      {(title || headerActions) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            pt: 1.5,
            pb: 1.5,
          }}
        >
          {title && (
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                lineHeight: 1.2,
                minWidth: 0,
                overflowWrap: 'anywhere',
              }}
            >
              {title}
            </Typography>
          )}
          {headerActions && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, ml: 'auto' }}>
              {headerActions}
            </Box>
          )}
        </Box>
      )}

      {hasNav && (
        <>
          <Box ref={tabsRef} sx={{ minWidth: 0 }}>
            <Tabs
              value={currentTabIndex >= 0 ? currentTabIndex : false}
              TabIndicatorProps={{ sx: { height: 2 } }}
              sx={{
                minHeight: 34,
                minWidth: 0,
                width: '100%',
                '& .MuiTabs-scroller': {
                  overflow: 'hidden !important',
                },
              }}
            >
              {displayedIndices.map((index) => {
                const button = visibleButtons[index];
                return (
                  <Tab
                    key={button.id ?? index}
                    value={index}
                    label={button.caption}
                    onClick={() => onNavigate?.(button.link!)}
                    sx={{
                      minHeight: 34,
                      minWidth: 0,
                      flexShrink: 1,
                      py: 0.5,
                      px: 1.5,
                      fontSize: '0.8125rem',
                      textTransform: 'none',
                    }}
                  />
                );
              })}
              {hasOverflow && (
                <Tab
                  value="more"
                  label={`More (${hiddenIndices.length})`}
                  onClick={(event) => setMoreAnchor(event.currentTarget)}
                  sx={{
                    minHeight: 34,
                    minWidth: 0,
                    flexShrink: 0,
                    py: 0.5,
                    px: 1.5,
                    fontSize: '0.8125rem',
                    textTransform: 'none',
                  }}
                />
              )}
            </Tabs>
          </Box>
          <Box
            ref={measurementRef}
            aria-hidden
            sx={{
              position: 'absolute',
              visibility: 'hidden',
              top: -10000,
              whiteSpace: 'nowrap',
            }}
          >
            {visibleButtons.map((button, index) => (
              <Box
                key={button.id ?? index}
                data-overflow-tab-index={index}
                sx={{ display: 'inline-flex', px: 1.5, fontSize: '0.8125rem' }}
              >
                {button.caption}
              </Box>
            ))}
          </Box>
          <Menu
            anchorEl={moreAnchor}
            open={Boolean(moreAnchor)}
            onClose={() => setMoreAnchor(null)}
          >
            {hiddenIndices.map((index) => (
              <MenuItem
                key={visibleButtons[index].id ?? index}
                selected={index === currentTabIndex}
                onClick={() => {
                  setMoreAnchor(null);
                  onNavigate?.(visibleButtons[index].link!);
                }}
              >
                {visibleButtons[index].caption}
              </MenuItem>
            ))}
          </Menu>
          <Divider />
        </>
      )}
    </Box>
  );
};
